import { LOCAL_STORAGE } from '@pray/shared/constants';

import { handleCookieBannerAccepted } from './cookieHelper';
import storage from './storage';

/** List of route patterns where tracking should always be enabled */
export const trackingWhitelistedRoutes = [
  // /^\/(.+?)\/donate\/?$/, // e.g. /artist_handle/donate
  // /^\/donate\/?$/, // e.g. /donate
  // /^\/download\/?$/, // e.g. /download
  // /^\/email-onboarding\/?$/, // e.g. /email-onboarding
  // /^\/lead\/?$/, // e.g. /lead
  // /^\/newsletter\/?$/, // e.g. /newsletter
  // /^\/onboarding\/?$/, // e.g. /onboarding
];

/**
 * Check if the current route is whitelisted
 * @returns {boolean} True if the route is whitelisted, false otherwise
 */
export function isTrackingWhitelistedRoute() {
  if (typeof window === 'undefined') return false;
  return trackingWhitelistedRoutes.some((regex) => regex.test(window.location.pathname));
}

/**
 * Check if the cookie banner should be shown
 * @returns {boolean} True if the cookie banner should be shown, false otherwise
 */
export function shouldShowCookieBanner() {
  return !isTrackingWhitelistedRoute();
}

/**
 * Check if the user has enabled Do Not Track in their browser
 * @returns {boolean} True if DNT is enabled, false otherwise
 */
function isDoNotTrackEnabled() {
  // Check for DNT header in navigator
  if (typeof navigator !== 'undefined' && navigator.doNotTrack) {
    return navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes';
  }

  // Check for DNT header in window
  if (typeof window !== 'undefined' && window.doNotTrack) {
    return window.doNotTrack === '1' || window.doNotTrack === 'yes';
  }

  // Check for msDoNotTrack (IE/Edge)
  if (typeof navigator !== 'undefined' && navigator.msDoNotTrack) {
    return navigator.msDoNotTrack === '1' || navigator.msDoNotTrack === 'yes';
  }

  return false;
}

function isCookieBannerAccepted() {
  const isAccepted = storage.getFromStorage(LOCAL_STORAGE.COOKIE_BANNER_IS_ACCEPTED) === 'true';
  return isAccepted;
}

function isCookieBannerDismissed() {
  const isDismissed = storage.getFromStorage(LOCAL_STORAGE.COOKIE_BANNER_IS_DISMISSED) === 'true';
  return isDismissed;
}

function updateCookieBannerAcceptance(isAccepted) {
  if (!isAccepted) {
    const cookieSettings = {
      isBranchOptOut: true,
      isFacebookOptOut: true,
      isGoogleOptOut: true,
      isLiveConnectOptOut: true,
      isHotjarOptOut: true,
      isDatadogOptOut: true,
    };

    updateCookieSettings(cookieSettings);
  }

  storage.upsertToStorage(LOCAL_STORAGE.COOKIE_BANNER_IS_ACCEPTED, isAccepted);
  storage.upsertToStorage(LOCAL_STORAGE.COOKIE_BANNER_IS_DISMISSED, true);

  if (isAccepted) handleCookieBannerAccepted();
}

function isUserInGDPRZone() {
  const date = new Date();
  const timezoneOffsetInMinutes = date.getTimezoneOffset();
  const timezoneOffsetInHours = timezoneOffsetInMinutes / 60;
  const isEUCountry = timezoneOffsetInHours >= -3 && timezoneOffsetInHours <= 1;
  const isPST = timezoneOffsetInHours === 7 || timezoneOffsetInHours === 8;

  return isEUCountry || isPST;
}

/**
 * Check if tracking should be disabled based on user preferences
 * @returns {boolean} True if tracking should be disabled, false otherwise
 */
function shouldDisableTracking() {
  // If the route is whitelisted, tracking should be enabled
  if (isTrackingWhitelistedRoute()) return false;

  // Check if user has explicitly opted out via cookie banner
  const hasOptedOut = !isCookieBannerAccepted();

  // Check if DNT is enabled
  const dntEnabled = isDoNotTrackEnabled();

  // Check if user is in GDPR zone and hasn't given consent
  const isInGDPRZone = isUserInGDPRZone();
  const hasGDPRConsent = isCookieBannerAccepted();

  // Check if user has opted out of all tracking services
  const cookieSettings = getCookieSettings();
  const allServicesOptedOut =
    cookieSettings.isBranchOptOut &&
    cookieSettings.isFacebookOptOut &&
    cookieSettings.isGoogleOptOut &&
    cookieSettings.isLiveConnectOptOut &&
    cookieSettings.isHotjarOptOut &&
    cookieSettings.isDatadogOptOut;

  return hasOptedOut || dntEnabled || (isInGDPRZone && !hasGDPRConsent) || allServicesOptedOut;
}

export const defaultCookieSettings = {
  isBranchOptOut: false,
  isFacebookOptOut: false,
  isGoogleOptOut: false,
  isLiveConnectOptOut: false,
  isHotjarOptOut: false,
  isDatadogOptOut: false,
};

/**
 * @typedef {Object} cookieSettings
 * @property {boolean} isBranchOptOut
 * @property {boolean} isFacebookOptOut
 * @property {boolean} isGoogleOptOut
 * @property {boolean} isLiveConnectOptOut
 * @property {boolean} isHotjarOptOut
 * @property {boolean} isDatadogOptOut
 *
 * Returns the serialized cookie settings
 * @returns {cookieSettings} cookieSettings
 */
function getCookieSettings() {
  const stringifiedCookieSettings = storage.getFromStorage(LOCAL_STORAGE.COOKIE_SETTINGS);
  const cookieSettings = stringifiedCookieSettings ? JSON.parse(stringifiedCookieSettings) : defaultCookieSettings;

  return cookieSettings;
}

function updateCookieSettings({
  isBranchOptOut = null,
  isFacebookOptOut = null,
  isGoogleOptOut = null,
  isLiveConnectOptOut = null,
  isHotjarOptOut = null,
  isDatadogOptOut = null,
}) {
  const currentCookieSettings = getCookieSettings();

  const newCookieSettings = { ...currentCookieSettings };
  if (isBranchOptOut !== null) {
    newCookieSettings.isBranchOptOut = isBranchOptOut;
  }
  if (isFacebookOptOut !== null) {
    newCookieSettings.isFacebookOptOut = isFacebookOptOut;
  }
  if (isGoogleOptOut !== null) {
    newCookieSettings.isGoogleOptOut = isGoogleOptOut;
  }
  if (isLiveConnectOptOut !== null) {
    newCookieSettings.isLiveConnectOptOut = isLiveConnectOptOut;
  }
  if (isHotjarOptOut !== null) {
    newCookieSettings.isHotjarOptOut = isHotjarOptOut;
  }
  if (isDatadogOptOut !== null) {
    newCookieSettings.isDatadogOptOut = isDatadogOptOut;
  }

  const stringifiedCookieSettings = JSON.stringify(newCookieSettings);
  storage.upsertToStorage(LOCAL_STORAGE.COOKIE_SETTINGS, stringifiedCookieSettings, false);

  return newCookieSettings;
}

export {
  isCookieBannerAccepted,
  isCookieBannerDismissed,
  updateCookieBannerAcceptance,
  isUserInGDPRZone,
  isDoNotTrackEnabled,
  shouldDisableTracking,
  getCookieSettings,
  updateCookieSettings,
};
